import React from "react";
import tw from "twin.macro";
import styled from "styled-components";

/**
 * Avatar d'auteur : affiche la photo si elle existe, sinon un monogramme (initiales)
 * dans le dégradé violet de la charte. Évite les trous dans la mise en page quand
 * aucune photo n'est publiée.
 *
 * @param {string}  name    nom complet, sert à dériver les initiales et le libellé accessible
 * @param {string}  [image] URL ou import d'image ; absent → monogramme
 * @param {number}  [size]  côté en pixels (48 par défaut)
 * @param {string}  [shape] "circle" (défaut) ou "rounded" (grand portrait carré arrondi)
 */

const Box = styled.div(({ $size, $shape }) => [
  tw`flex-shrink-0 flex items-center justify-center overflow-hidden bg-gray-200`,
  $shape === "rounded" ? tw`rounded-lg` : tw`rounded-full`,
  `width: ${$size}px; height: ${$size}px;`,
]);

const Photo = tw.img`w-full h-full object-cover`;

const Monogram = styled.span(({ $size }) => [
  tw`w-full h-full flex items-center justify-center font-black text-gray-100 select-none tracking-wide`,
  `background: linear-gradient(135deg, #6415FF 0%, #3c0d99 100%);`,
  `font-size: ${Math.round($size * 0.38)}px; line-height: 1;`,
]);

/** « Jimmy Inthalangsy » → « JI » ; « Besançon » → « B ». */
export const initialsOf = name =>
  String(name || "")
    .trim()
    .split(/[\s-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(word => word[0].toUpperCase())
    .join("");

export default function Avatar({ name = "", image, size = 48, shape = "circle", className }) {
  const initials = initialsOf(name);
  return (
    <Box $size={size} $shape={shape} className={className}>
      {image ? (
        <Photo src={image} alt="" loading="lazy" decoding="async" />
      ) : (
        <Monogram $size={size} role="img" aria-label={name ? `Portrait de ${name}` : undefined}>
          {initials}
        </Monogram>
      )}
    </Box>
  );
}
